import { Color, Vector3 } from "three";
import { nextFrame } from "../../../library-js/animation/nextFrame";
import { OnAnimationFrame } from "../../../library-js/animation/OnAnimationFrame";
import { sleep } from "../../../library-js/animation/sleep";
import { HSLColor } from "../../../library-js/colors/HSLColor";
import { YBColorSpace } from "../../../library-js/colors/YBColorSpace";
import { GradientText } from "../../../library-js/dom-actions/GradientText";
import { AttributeValue } from "../../../library-js/dom-tools/AttributeValue";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { OnChange } from "../../../library-js/dom-tools/EventListeners";
import { ColorStop } from "../../../library-js/dom-ui/basics/ColorStop";
import { RadialGradient, LinearGradient } from "../../../library-js/dom-ui/basics/Gradient";
import { MathX } from "../../../library-js/math/MathX";
import { Arrays } from "../../../library-js/tools/Arrays";
import { VerticalSlider, VerticalSliderElement } from "../../../library-templates/sliders/VerticalSlider";
import { title } from "process";

export class VijoPartyValues
{
  color = new HSLColor(0,0,0,1);
  offset = new Vector3();
  clamp = false;
  smoothHue = false;
  smoothHueRadius = 30;
  flipped = false;
  blocks = 3;

  getColorA()
  {
    let off = this.offset.clone().multiplyScalar( -1 );
    let clone = this.color.clone();
    clone.shift( off.x, off.y, off.z );
    return clone;
  }

  getColorB()
  {
    let off = this.offset;
    let clone = this.color.clone();
    clone.shift( off.x, off.y, off.z );
    return clone;
  }
}

export class VijoParty
{
  static type = new ElementType( "vijo-settings" );
  static valueSlider = new ElementType( "value-slider");
  static valueDisplay = new ElementType( "value-display" );

  static colorBlocks = new ElementType( "color-blocks" );
  static colorBlock =  new ElementType<HTMLElement>( "color-block" );
  static animateHue = new ElementAttribute( "animate-hue" );
  static interpolation = new ElementAttribute( "interpolation" );

  static linear = new AttributeValue( ElementAttribute.value, "Linear" );
  static linearFlipped = new AttributeValue( ElementAttribute.value, "Linear-Flipped" );
  static clamped = new AttributeValue( ElementAttribute.value, "Clamped" );

  static clampedBlended15 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-15" );
  static clampedBlended30 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-30" );
  static clampedBlended60 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-60" );
  static clampedBlendedFast15 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-Fast-15" );
  static clampedBlendedFast30 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-Fast-30" );
  static clampedBlendedFast60 = new AttributeValue( ElementAttribute.value, "Clamped-Blended-Fast-60" );


  static range = new ElementAttribute( "range" );
  static value = new ElementAttribute( "value" );
  static formula = new ElementAttribute( "formula" );
  static fraction = new ElementAttribute( "fraction" );
  static sign = new ElementAttribute( "sign" );
  static animationCallbackAssigned = false;
  static animatedElements:HTMLElement[] = [];

  static applyOnDocument( onAnimationFrame:OnAnimationFrame )
  {
    VijoParty.type.forAllInDoc( e => VijoParty.applyOnElement( e ) );
  }


  static async applyOnElement( e:Element )
  {
    // if ( VijoParty.animateHue.isQueriedChecked( e ) )
    // {
    //   Arrays.pushIfNotInside( VijoParty.animatedElements, e as HTMLElement );
    // }

    let sliders = this.valueSlider.queryAll( e );
    sliders.forEach( s => VijoParty.assignSlider( e, s as HTMLElement ) );

    // OnChange.add( VijoParty.interpolation.query<HTMLSelectElement>( e ), ()=>this.updateBlocks( e ) );
    // OnChange.add( VijoParty.animateHue.query( e ), 
    // ()=>
    // {
    //   let animated = VijoParty.animateHue.isQueriedChecked( e );

    //   if ( animated )
    //   {
    //     Arrays.pushIfNotInside( VijoParty.animatedElements, e as HTMLElement );
    //   }
    //   else
    //   {
    //     Arrays.remove( VijoParty.animatedElements, e as HTMLElement );
    //   }
    // } 
    // );

    await sleep( 500 );
    this.updateBlocks( e ); 
  }

  static updateBlocks( e:Element )
  {
    let values = new VijoPartyValues();
    values.color = new HSLColor( 
      this.getSliderValue( e, new ElementAttribute( "h" ) ),
      this.getSliderValue( e, new ElementAttribute( "s" ) ),
      this.getSliderValue( e, new ElementAttribute( "l" ) ),
      1
    );

    values.offset = new Vector3(
      this.getSliderValue( e, new ElementAttribute( "offset-h" ) ),
      this.getSliderValue( e, new ElementAttribute( "offset-s" ) ),
      this.getSliderValue( e, new ElementAttribute( "offset-l" ) ),
    );

    let bgElement = new ElementType<HTMLElement>( "vijo-invitation" ).queryDoc();
    bgElement.style.background = LinearGradient.create( [ values.getColorA(), values.getColorB() ] ).toString();


    let titleElement = new ElementType<HTMLElement>( "big--title" ).queryDoc();
    let titleTop = this.getSliderValue( e, new ElementAttribute( "font-y" ) )
    titleElement.style.top = `calc( ${titleTop} * var(--sw))`;

    let titleColor = new HSLColor( 
      this.getSliderValue( e, new ElementAttribute( "font-h" ) ),
      this.getSliderValue( e, new ElementAttribute( "font-s" ) ),
      this.getSliderValue( e, new ElementAttribute( "font-l" ) ),
      1
    );

    let copyColor = new HSLColor( 
      this.getSliderValue( e, new ElementAttribute( "font2-h" ) ),
      this.getSliderValue( e, new ElementAttribute( "font2-s" ) ),
      this.getSliderValue( e, new ElementAttribute( "font2-l" ) ),
      1
    );

    let outlineColor = new HSLColor( 
      this.getSliderValue( e, new ElementAttribute( "font-outline-h" ) ),
      this.getSliderValue( e, new ElementAttribute( "font-outline-s" ) ),
      this.getSliderValue( e, new ElementAttribute( "font-outline-l" ) ),
      1
    );

    let glowColor = new HSLColor( 
      this.getSliderValue( e, new ElementAttribute( "font-glow-h" ) ),
      this.getSliderValue( e, new ElementAttribute( "font-glow-s" ) ),
      this.getSliderValue( e, new ElementAttribute( "font-glow-l" ) ),
      1
    );

    let fontTable = 
    [ 
      "Jost", 
      "Righteous",
      "Playwrite HU", 
      "Pacifico", 
      "Ole", 
      "Tiny5",
      "Jersey 10",
      "Jacquard 12",
      "Emilys Candy",
      "Bigelow Rules",
      "Miltonian Tattoo",
      "Mountains of Christmas",
      "Barrio",
      "Kirang Haerang",
      "Henny Penny",
      "Flavors",
      "Butterfly Kids",
      "Shadows Into Light",
      "Coming Soon",
      "The Girl Next Door",
      "Mynerve",
      "Borel",
      "Waiting for the Sunrise",
      "Give You Glory",
      "Just Another Hand", 
      "Reenie Beanie", "Square Peg",
    ];


    let fontSize = this.getSliderValue( e, new ElementAttribute( "font-size" ) );
    let fontOutlineSize = this.getSliderValue( e, new ElementAttribute( "font-outline-size" ) );
    let fontOutlineSharpness = this.getSliderValue( e, new ElementAttribute( "font-outline-sharpness" ) );
    let fontFamily = fontTable[ Math.round( this.getSliderValue( e, new ElementAttribute( "font-family" ) ) - 1 ) ]; 
    let font2Family = fontTable[ Math.round( this.getSliderValue( e, new ElementAttribute( "font2-family" ) ) - 1 ) ]; 

    let fontSelectionElement = new ElementType<HTMLElement>( "font-selection" ).queryDoc();
    fontSelectionElement.innerHTML = `${fontFamily}, ${font2Family}`;
    let notSharp = 0.05;
    let sharp = 0.001;

    let sharpnessValue = MathX.lerp( notSharp, sharp, fontOutlineSharpness / 100 ); 
    
    titleElement.style.fontSize = `calc( ${fontSize/100} * 10em )`;
    titleElement.style.color = titleColor.toString();
    titleElement.style.fontFamily = `"${fontFamily}"`;

  
    let filter = `drop-shadow(0 0 ${sharpnessValue}em ${outlineColor})`;
    let filterMultiple = "";

    for ( let i = 0; i < fontOutlineSize; i++ )
    {
      if ( i != 0 )
      {
        filterMultiple +=" ";
      }

      filterMultiple += filter;
    }

    let fontGlowSize = this.getSliderValue( e, new ElementAttribute( "font-glow-size" ) );
    let fontGlowOffset = this.getSliderValue( e, new ElementAttribute( "font-glow-offset" ) );
    let glow = `drop-shadow(0 ${fontGlowOffset/100}em ${fontGlowSize/100}em ${glowColor})`;

    titleElement.style.filter = filterMultiple + " " + glow + " " + glow;

    let copyElement = new ElementType<HTMLElement>( "invitation--copy" ).queryDoc();
    let copyOffset = this.getSliderValue( e, new ElementAttribute( "font2-y" ) ) + titleTop;
    copyElement.style.top = `calc( ${copyOffset} * var(--sw))`;
    let fontSize2 = this.getSliderValue( e, new ElementAttribute( "font2-size" ) )
    copyElement.style.fontSize = `calc( ${fontSize2/100} * 5em )`;
    copyElement.style.color = copyColor.toString();

    copyElement.style.fontFamily = `"${font2Family}"`;

  }

  static getSlider( e:Element, attribute:ElementAttribute )
  {
    let element = attribute.query( e );
    let slider = VerticalSlider.type.query( element ) as VerticalSliderElement;

    return slider
  }

  static getSliderValue( e:Element, attribute:ElementAttribute, alt:number = 0 )
  {
    // console.log( e, attribute, attribute.query( e ) );

    let element = attribute.query( e );

    if ( element == null )
    {
      return alt;
    }

    let slider = VerticalSlider.type.query( element ) as VerticalSliderElement;

    if ( slider == null )
    {
      return alt;
    }

    let range  = VijoParty.range.parseNumbers( element );

    

    return MathX.map( slider._value.value.value, 0, 1, range[ 0 ], range[ 1 ] ); 
  }

  static async assignSlider( parent:Element, e:HTMLElement )
  {
    let slider = VerticalSlider.type.query( e ) as VerticalSliderElement;

    let display = VijoParty.valueDisplay.query( e );
    
    let range    = VijoParty.range.parseNumbers( e );
    let value    = VijoParty.value.parseNumbers( e );
    let fraction = VijoParty.fraction.parseNumbers( e );
    let sign     = VijoParty.sign.in( e );
    

    let formula = VijoParty.formula.from( e, "${value}" );
 
    let format = ( realValue:number ) => 
    {
      let valueString = realValue + "";

      if ( fraction != null )
      {
        if ( fraction[ 0 ] == 0 )
        {
          valueString = Math.round( realValue ) + "";
        }
        else 
        {
          valueString = realValue.toFixed( fraction[ 0 ] );
        }
      }

      if ( sign )
      {
        if ( realValue > 0 )
        {
          valueString = "-/+" + valueString;
        }
        else
        {
          valueString = "+/-" + ( valueString.replace( "-", "" ) );
        }
        
      }

      return valueString;
    };

    

    slider._value.value.addListener(
      ( )=>
      {
        let normalizedValue = slider._value.value.value;
        let realValue = MathX.map( normalizedValue, 0, 1, range[ 0 ], range[ 1 ] );
        let ybValue = YBColorSpace.hueToYB( realValue );

        let valueString = format( realValue );
        let ybString    = format( ybValue );      
        

        display.innerHTML = formula.replace( "${value}", valueString ).replace( "${ybValue}", ybString);
      }
    );
 
   

    await nextFrame();
    let normalized = MathX.normalize( value[ 0 ], range[ 0 ], range[ 1 ] );
    slider._value.value.value = normalized; 
      

    //console.log( "Set value:", value[ 0 ], normalized, range );

    await sleep( 500 );
    slider._value.value.forceDispatch();

    await sleep( 500 );
    slider._value.value.forceDispatch();

    await sleep( 500 );
    slider._value.value.forceDispatch();


    slider._value.value.addListener(
      ()=>
      {
        this.updateBlocks( parent );
      }
    );
  }

}